import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { getComponentForEnumValue } from "./verify-wallet/index.js";
import { callAPI, urlSearchParamsToObject } from "./utils";
import {
  decodePaymentData,
  getPaymentDataFromUrl,
} from "./paymentData.utils.js";
import { getPaymentErrorMessage } from "./paymentErrorMessages.js";

function VerifyingWallet() {
  const [alertMessage, setAlertMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  let [params] = useSearchParams();
  const data = params.get("data");
  const { token, gateway: routeGateway } = useParams();
  const [componentInfo, setComponentInfo] = useState({
    component: null,
    props: {},
  });

  useEffect(() => {
    setIsLoading(true);
    getPaymentGateWay(data ?? token, routeGateway);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, token, routeGateway]);

  const alert = (message) => {
    setAlertMessage(message);
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(null);
  };

  const getPaymentGateWay = async (token, gateway) => {
    try {
      var searchParams = decodePaymentData(token);
      var params = urlSearchParamsToObject(searchParams, null);
      // Short-form: `f`; long-form wallet URLs: `fleetToken`.
      var userId = params.i;
      var userType = params.t;
      var returnUrl = params.u;
      var returnUrlFailed = params.r;
      const payloadGateway = params.gateway ?? params.g ?? gateway;

      // Enrich only short-form (`f`); long-form payloads are already complete.
      if (params.f) {
        const data = await callAPI(
          `${process.env.REACT_APP_WEBHOOK_API_ENDPOINT}/api/webhook/genericCreditForm`,
          "POST",
          {
            fleetToken: params.f,
            userId,
            userType,
            returnUrl,
            returnUrlFailed,
            gateway: payloadGateway,
            type: "wallet",
          }
        );
        if (!data?.response) {
          throw new Error(
            getPaymentErrorMessage(
              data,
              `Unable to load wallet form (${data?.returnCode ?? "unknown"})`
            )
          );
        }
        if (data.response.redirect) {
          if (data.response.returnWebUrl) {
            window.location.href = data.response.returnWebUrl;
          } else if (data.response["3ds_url"]) {
            window.location.href = data.response["3ds_url"];
          } else {
            setAlertMessage("Something went wrong, no redirect URL!");
            setIsLoading(false);
          }
        } else {
          searchParams = getPaymentDataFromUrl(data.response.returnWebUrl);
          params = { ...params, ...data.response };
          params = urlSearchParamsToObject(searchParams, params);
        }
      }

      const fleetToken = params.f ?? params.fleetToken;
      userId = params.i ?? params.userId ?? userId;
      userType = params.t ?? params.userType ?? userType;
      returnUrl = params.u ?? params.returnUrl ?? returnUrl;
      returnUrlFailed = params.r ?? params.returnUrlFailed ?? returnUrlFailed;
      gateway = params.gateway ?? gateway;
      if (gateway) {
        let component = getComponentForEnumValue(gateway);
        if (component) {
          setComponentInfo({
            component,
            props: {
              ...params,
              ...{
                fleetToken,
                userId,
                userType,
                returnUrl,
                returnUrlFailed,
                searchParams,
              },
            },
          });
          setIsLoading(false);
        }
      }
    } catch (error) {
      setAlertMessage(
        [error?.method, error?.message].filter(Boolean).join(" ") ||
          "Unable to load wallet form"
      );
      setIsLoading(false);
    }
  };

  const { component: Component, props } = componentInfo;
  return (
    <div>
      {Component ? (
        <Component {...props} alert={alert} setIsLoading={setIsLoading} />
      ) : null}
      <Snackbar
        open={!!alertMessage}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        xl={{
          position: "fixed",
          top: 10,
          left: 10,
        }}
      >
        <Alert onClose={handleCloseAlert} severity="error" variant="filled">
          {alertMessage}
        </Alert>
      </Snackbar>

      {isLoading && (
        <div id="loading">
          <CircularProgress />
        </div>
      )}
    </div>
  );
}

export default VerifyingWallet;
